/**
 * Bani, TVA si rotunjiri.
 *
 * Toate sumele circula ca INTREGI in unitati minore (bani; 1 RON = 100 bani).
 * Motivul e ADR-004: suma liniilor de pe bon trebuie sa dea exact totalul
 * incasat, altfel casa de marcat refuza bonul. Cu float, 0.1 + 0.2 pe douazeci
 * de linii produce diferente de un ban care se descopera la casa, nu in test.
 *
 * Regula de conversie la frontiera cu POS-ul: POS-ul lucreaza pe `numeric`
 * serializat ca string ("34.00"). Se traduce EXCLUSIV prin `fromDecimalString`
 * / `toDecimalString`, niciodata prin `Number(...)`.
 */
export const MINOR_UNITS_PER_MAJOR = 100;
/** Cotele de TVA valabile in Romania dupa reforma din 2025. */
export const VAT_RATES_RO = [0, 11, 21];
/** ADR-004: taxa de livrare e transport, nu mancare. */
export const VAT_RATE_DELIVERY_FEE = 21;
/** Cota implicita pentru mancare preparata. Cota reala sta pe executie, per firma. */
export const VAT_RATE_FOOD_DEFAULT = 11;
/** ADR-019: comisionul e prestare de intermediere, cota standard. */
export const VAT_RATE_COMMISSION = 21;
export const CURRENCY_RON = "RON";
export function isVatRateRo(value) {
    return typeof value === "number" && VAT_RATES_RO.includes(value);
}
export function assertVatRateRo(value, field = "cota de TVA") {
    if (!isVatRateRo(value)) {
        throw new Error(`Valoare invalida pentru ${field}: ${String(value)}. Cotele acceptate sunt ${VAT_RATES_RO.join(", ")}.`);
    }
    return value;
}
export function isMoneyMinor(value) {
    return typeof value === "number" && Number.isSafeInteger(value);
}
export function assertMoneyMinor(value, field = "suma") {
    if (!isMoneyMinor(value)) {
        throw new Error(`Valoare invalida pentru ${field}: ${String(value)}. Sumele se exprima in bani, ca numere intregi.`);
    }
    return value;
}
export function assertNonNegativeMoney(value, field = "suma") {
    const amount = assertMoneyMinor(value, field);
    if (amount < 0) {
        throw new Error(`Valoare invalida pentru ${field}: ${amount}. Suma nu poate fi negativa.`);
    }
    return amount;
}
/**
 * Impartire cu rotunjire la jumatate departe de zero (regula fiscala romaneasca),
 * facuta integral pe intregi ca sa nu intre niciun float in lantul de calcul.
 */
export function divideRound(numerator, denominator) {
    assertMoneyMinor(numerator, "numaratorul");
    if (!Number.isSafeInteger(denominator) || denominator === 0) {
        throw new Error(`Numitor invalid la calculul de bani: ${String(denominator)}.`);
    }
    const negative = numerator < 0 !== denominator < 0;
    const n = Math.abs(numerator);
    const d = Math.abs(denominator);
    const quotient = Math.floor(n / d);
    const remainder = n - quotient * d;
    const rounded = remainder * 2 >= d ? quotient + 1 : quotient;
    return negative ? -rounded : rounded;
}
export function addMoney(...values) {
    let total = 0;
    for (const value of values) {
        total += assertMoneyMinor(value);
    }
    return assertMoneyMinor(total, "totalul");
}
export function subtractMoney(minuend, subtrahend) {
    return assertMoneyMinor(assertMoneyMinor(minuend) - assertMoneyMinor(subtrahend), "diferenta");
}
export function multiplyMoney(amount, quantity) {
    assertMoneyMinor(amount, "pretul unitar");
    if (!Number.isSafeInteger(quantity) || quantity < 0) {
        throw new Error(`Cantitate invalida: ${String(quantity)}. Cantitatea se exprima ca numar intreg pozitiv.`);
    }
    return assertMoneyMinor(amount * quantity, "totalul de linie");
}
/** TVA-ul continut intr-o suma cu TVA inclus (preturile de raft sunt brute). */
export function vatFromGross(gross, rate) {
    assertMoneyMinor(gross, "suma bruta");
    assertVatRateRo(rate);
    if (rate === 0)
        return 0;
    return divideRound(gross * rate, 100 + rate);
}
export function netFromGross(gross, rate) {
    return subtractMoney(gross, vatFromGross(gross, rate));
}
export function grossFromNet(net, rate) {
    assertMoneyMinor(net, "suma neta");
    assertVatRateRo(rate);
    if (rate === 0)
        return net;
    return addMoney(net, divideRound(net * rate, 100));
}
/**
 * Procentele (comision, subventie) vin din configurare ca zecimale text
 * ("12.50"), nu ca float. Se convertesc in puncte de baza intregi.
 */
export function parsePercentToBps(percent, field = "procentul") {
    const text = typeof percent === "number" ? percent.toString() : percent.trim();
    const match = /^(-?)(\d+)(?:[.,](\d{1,2}))?$/.exec(text);
    if (!match) {
        throw new Error(`Valoare invalida pentru ${field}: "${text}". Format asteptat: numar cu maximum doua zecimale, de exemplu "12.50".`);
    }
    const sign = match[1] === "-" ? -1 : 1;
    const whole = Number.parseInt(match[2] ?? "0", 10);
    const fraction = (match[3] ?? "").padEnd(2, "0");
    return sign * (whole * 100 + Number.parseInt(fraction, 10));
}
export function applyPercent(amount, percent, field = "procentul") {
    assertMoneyMinor(amount, "baza de calcul");
    const bps = parsePercentToBps(percent, field);
    return divideRound(amount * bps, 10_000);
}
/**
 * ADR-019: comisionul nu e un numar, e o factura. Se calculeaza net, cu TVA
 * propriu peste el, pe baza pretului de vanzare al vendorului.
 */
export function commissionOnSale(saleGross, commissionPercent, vatRate = VAT_RATE_COMMISSION) {
    const base = assertNonNegativeMoney(saleGross, "baza de comision");
    // Un procent negativ ar produce un comision negativ, adica platforma ar
    // datora bani vendorului pe post de comision. Se opreste aici, nu la CHECK-ul
    // din baza, unde eroarea nu ar mai spune ce configurare a produs-o.
    const bps = parsePercentToBps(commissionPercent, "procentul de comision");
    if (bps < 0) {
        throw new Error(`Procentul de comision nu poate fi negativ: "${String(commissionPercent)}".`);
    }
    const net = divideRound(base * bps, 10_000);
    const rate = assertVatRateRo(vatRate, "cota de TVA a comisionului");
    const vatAmount = rate === 0 ? 0 : divideRound(net * rate, 100);
    return { base, net, vatRate: rate, vatAmount, gross: addMoney(net, vatAmount) };
}
/**
 * Imparte o suma pe ponderi, cu metoda restului cel mai mare, astfel incat
 * suma bucatilor sa fie EXACT totalul. Folosit la alocarea reducerilor si a
 * subventiilor pe linii; taxa de livrare NU se imparte (ADR-004).
 */
export function splitProportional(total, weights) {
    assertMoneyMinor(total, "suma de impartit");
    if (weights.length === 0) {
        throw new Error("Impartirea proportionala are nevoie de cel putin o pondere.");
    }
    for (const weight of weights) {
        if (!Number.isSafeInteger(weight) || weight < 0) {
            throw new Error(`Pondere invalida la impartirea proportionala: ${String(weight)}.`);
        }
    }
    const weightSum = weights.reduce((acc, weight) => acc + weight, 0);
    if (weightSum === 0) {
        throw new Error("Impartirea proportionala are nevoie de cel putin o pondere nenula.");
    }
    const shares = [];
    const remainders = [];
    let allocated = 0;
    for (let index = 0; index < weights.length; index += 1) {
        const weight = weights[index] ?? 0;
        const exact = total * weight;
        const share = Math.trunc(exact / weightSum);
        shares.push(share);
        allocated += share;
        remainders.push({ index, remainder: Math.abs(exact - share * weightSum) });
    }
    let leftover = total - allocated;
    const step = leftover < 0 ? -1 : 1;
    remainders.sort((a, b) => b.remainder - a.remainder || a.index - b.index);
    let cursor = 0;
    while (leftover !== 0) {
        const target = remainders[cursor % remainders.length];
        if (!target)
            break;
        const current = shares[target.index] ?? 0;
        shares[target.index] = current + step;
        leftover -= step;
        cursor += 1;
    }
    return shares;
}
/** Traduce un `numeric` din POS ("34.00", "34", "34.5") in bani. */
export function fromDecimalString(value, field = "suma") {
    const text = typeof value === "number" ? value.toString() : value.trim();
    const match = /^(-?)(\d+)(?:[.,](\d{1,2}))?$/.exec(text);
    if (!match) {
        throw new Error(`Valoare monetara invalida pentru ${field}: "${text}". Format asteptat: numar cu maximum doua zecimale.`);
    }
    const sign = match[1] === "-" ? -1 : 1;
    const whole = Number.parseInt(match[2] ?? "0", 10);
    const fraction = (match[3] ?? "").padEnd(2, "0");
    return sign * (whole * MINOR_UNITS_PER_MAJOR + Number.parseInt(fraction, 10));
}
/** Traduce bani in `numeric` pentru POS. Intotdeauna doua zecimale. */
export function toDecimalString(amount) {
    assertMoneyMinor(amount);
    const sign = amount < 0 ? "-" : "";
    const absolute = Math.abs(amount);
    const whole = Math.trunc(absolute / MINOR_UNITS_PER_MAJOR);
    const fraction = absolute - whole * MINOR_UNITS_PER_MAJOR;
    return `${sign}${whole}.${fraction.toString().padStart(2, "0")}`;
}
/**
 * Formatare pentru interfata, in conventia romaneasca: 1.234,56 lei.
 * Nu se foloseste Intl aici pentru ca datele ICU difera intre imaginea Docker
 * si browser, iar totalul afisat la checkout trebuie sa fie identic peste tot.
 */
export function formatRon(amount, options = {}) {
    assertMoneyMinor(amount);
    const withCurrency = options.withCurrency !== false;
    const sign = amount < 0 ? "-" : "";
    const absolute = Math.abs(amount);
    const whole = Math.trunc(absolute / MINOR_UNITS_PER_MAJOR);
    const fraction = absolute - whole * MINOR_UNITS_PER_MAJOR;
    const grouped = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    const body = `${sign}${grouped},${fraction.toString().padStart(2, "0")}`;
    return withCurrency ? `${body} lei` : body;
}
//# sourceMappingURL=money.js.map